'use client';

import React, { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { solicitudService } from '@/lib/api/solicitudService';
import { Solicitud } from '@/types/solicitud';
import { SolicitudTable } from './SolicitudTable';
import { SolicitudDetail } from './SolicitudDetail';
import { useSolicitudFilter, SolicitudFilterUI } from './solicitudFilters';
import { Button } from '@/components/ui/Button';
import ErrorMessage from '@/components/common/ErrorMessage';
import { useAuth } from '@/hooks/useAuth';
import { userService } from '@/lib/api/userService';
import { User } from '@/types/user';
import { Badge } from '@/components/ui/Badge';

const EntregadosARiesgosManagement: React.FC = () => {
  const { user: authUser } = useAuth();
  const pathname = usePathname();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [solicitudes, setSolicitudes] = useState<Solicitud[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedSolicitud, setSelectedSolicitud] = useState<Solicitud | null>(null);

  const { filtered, baseList, ...filterProps } = useSolicitudFilter(solicitudes);

  // Configuración del módulo
  const viewName = "Entregados a Riesgos";
  const fixedStatus = "ENTREGA A RIESGOS";
  const areaResponsable = "RIESGOS";
  const fechaReferencia = "fechaEntregaARiesgos";

  const authSub = authUser?.sub ?? null;

  const fetchSolicitudes = async () => {
    if (!authSub) return;
    try {
      setIsLoading(true);
      setError(null);
      const response = await solicitudService.getEntregadosARiesgos(authSub);
      if (response.data) {
        setSolicitudes(response.data || []);
      } else {
        setError(response.error || 'Error al cargar las solicitudes');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar las solicitudes');
      if (process.env.NODE_ENV === 'development') console.error('Error fetching solicitudes:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!authSub) return;
    userService.getUser(authSub).then((response) => {
      if (response.data) setCurrentUser(response.data);
    }).catch((err) => {
      if (process.env.NODE_ENV === 'development') console.error('Error cargando usuario:', err);
    });
  }, [authSub]);

  useEffect(() => {
    if (!authSub) return;
    fetchSolicitudes();
  }, [authSub, pathname]);

  const handleEdit = (solicitud: Solicitud) => {
    if (!solicitud.id) return;
    setError(null);
    setSelectedSolicitud(solicitud);
    setIsDetailOpen(true);
  };

  const handleUpdateDetail = (updated: Solicitud) => {
    setSelectedSolicitud(updated);
    fetchSolicitudes();
  };

  // Función para obtener el badge del estatus de análisis
  const getEstatusAnalisisBadge = (estatus: string | undefined): "info" | "success" | "warning" | "danger" => {
    const est = (estatus || "").toUpperCase();
    if (est.includes("CONCLUIDO") || est.includes("TERMINADO")) return "success";
    if (est.includes("PENDIENTE") || est.includes("EN ESPERA")) return "warning";
    if (est.includes("RECHAZADO") || est.includes("CANCELADO")) return "danger";
    return "info";
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Entregados a Riesgos</h1>
          <p className="text-sm text-gray-500 mt-1">
            Solicitudes con estatus ENTREGA A RIESGOS
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={fetchSolicitudes} disabled={isLoading}>
            {isLoading ? "Cargando…" : "Recargar"}
          </Button>
        </div>
      </div>

      {/* Filters - mismo que en Solicitudes */}
      <SolicitudFilterUI
        {...filterProps}
        filteredCount={filtered.length}
        baseCount={baseList.length}
        countLabel="solicitudes entregadas a riesgos"
      />
      <div className="text-xs text-gray-500 -mt-2 px-1">Estatus: {fixedStatus} | Área: {areaResponsable}</div>

      {/* Content */}
      {isLoading ? (
        <div className="flex justify-center items-center h-64 bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-600" />
        </div>
      ) : error ? (
        <ErrorMessage message={error} onRetry={fetchSolicitudes} />
      ) : (
        <>
          {filtered.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-10 text-center">
              <div className="text-sm font-semibold text-gray-900">Sin solicitudes entregadas a riesgos</div>
              <div className="mt-1 text-sm text-gray-500">
                No hay solicitudes con estatus ENTREGA A RIESGOS. Ajusta los filtros.
              </div>
              <div className="mt-4 flex justify-center gap-2">
                <Button variant="secondary" onClick={filterProps.clearFilters}>
                  Limpiar búsqueda
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* Resumen de análisis */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 p-4 rounded-xl">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="text-center">
                    <p className="text-xs text-gray-600 uppercase mb-1">Total Solicitudes</p>
                    <p className="text-2xl font-bold text-gray-900">{filtered.length}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-600 uppercase mb-1">Con Analista Asignado</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {filtered.filter(s => s.analistaNombre && s.analistaNombre.trim() !== '').length}
                    </p>
                  </div>
                </div>
              </div>

              {/* Tabla con indicadores visuales */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <SolicitudTable
                  solicitudes={filtered}
                  onEdit={handleEdit}
                  renderActions={(s) => (
                    <div className="flex items-center gap-2 flex-wrap">
                      {s.analistaNombre && (
                        <Badge variant="info" className="text-xs">
                          Analista: {s.analistaNombre}
                        </Badge>
                      )}
                      {s.estatusAnalisis && (
                        <Badge variant={getEstatusAnalisisBadge(s.estatusAnalisis)} className="text-xs">
                          {s.estatusAnalisis}
                        </Badge>
                      )}
                    </div>
                  )}
                />
              </div>
              <div className="text-right text-xs text-gray-400 px-2">
                Total: {filtered.length} solicitudes entregadas a riesgos
              </div>
            </>
          )}

          <SolicitudDetail
            isOpen={isDetailOpen}
            onClose={() => setIsDetailOpen(false)}
            solicitud={selectedSolicitud}
            onUpdate={handleUpdateDetail}
            modulo="entregados-riesgos"
          />
        </>
      )}
    </div>
  );
};

export default EntregadosARiesgosManagement;
